import { sql } from "drizzle-orm";
import { pgTable, text, varchar, uuid, timestamp, numeric, integer, date, pgEnum, boolean, jsonb, uniqueIndex } from "drizzle-orm/pg-core";
import { createInsertSchema, createSelectSchema } from "drizzle-zod";
import { z } from "zod";

// Cross-domain imports
import { deals, facilities } from "./schema-deals.js";
import { contacts, accounts } from "./schema-crm.js";
import { lenders } from "./schema-lenders.js";
import { organisations } from "./schema-security.js";

// ============================================================================
// SECURITY & PROPERTY MODULE TABLES (14 tables + 2 views) - SPRINT-5
// Collateral tracking, guarantees, LTV calculations, WALE analysis
// Unblocks Sprint 3 facility_parties.guarantee_id FK
// PRD Reference: docs/prds/PRD-SECURITY-PROPERTY.md
// ============================================================================

// ============================================================================
// ENUMS - Security & Property (20 enums)
// ============================================================================

// Security Type (7 types) - ISSUE-145: Added guarantee, general_security_agreement
export const securityTypeEnum = pgEnum("security_type_enum", [
  "residential_property",
  "commercial_property",
  "vehicle",
  "equipment",
  "financial_asset",
  "guarantee",
  "general_security_agreement"
]);

// Collateral Security Type (SEC-RQ1: 7 types)
export const collateralSecurityTypeEnum = pgEnum("collateral_security_type_enum", [
  "registered_mortgage",
  "specific_security_agreement",
  "general_security_agreement",
  "ppsr_registration",
  "term_deposit",
  "caveat",
  "residual_stock"
]);

// Security Status (SEC-RQ7: 9 lifecycle states)
export const securityStatusEnum = pgEnum("security_status_enum", [
  "identified",
  "proposed",
  "valued",
  "approved",
  "registered",
  "varied",
  "substituted",
  "discharged",
  "cancelled"
]);

// Security Position (SEC-RQ3)
export const securityPositionEnum = pgEnum("security_position_enum", [
  "first",
  "second",
  "third",
  "subordinated"
]);

// Ownership Basis (SEC-RQ4)
export const ownershipBasisEnum = pgEnum("ownership_basis_enum", [
  "sole",
  "joint_tenants",
  "tenants_in_common"
]);

// Guarantee Type (SEC-RQ1: 8 types)
export const guaranteeTypeEnum = pgEnum("guarantee_type_enum", [
  "personal_unlimited",
  "personal_limited",
  "personal_limited_percentage",
  "personal_several",
  "personal_joint_several",
  "corporate",
  "corporate_several",
  "corporate_joint_several"
]);

// Liability Type
export const liabilityTypeEnum = pgEnum("liability_type_enum", [
  "unlimited",
  "limited_amount",
  "limited_percentage",
  "several",
  "joint_and_several",
  "all_assets"
]);

// Guarantee Status
export const guaranteeStatusEnum = pgEnum("guarantee_status_enum", [
  "proposed",
  "confirmed",
  "executed",
  "released"
]);

// Valuation Source
export const valuationSourceEnum = pgEnum("valuation_source_enum", [
  "avm_corelogic",
  "avm_proptrack",
  "avm_valocity",
  "full_valuation",
  "desktop_valuation",
  "kerbside_valuation",
  "contract_price",
  "bank_valuation",
  "self_assessed",
  "redbook",
  "glass_guide",
  "manual_entry"
]);

// Australian State (reused from existing, but defined here if not exists)
export const australianStateEnum = pgEnum("australian_state_enum", [
  "NSW",
  "VIC",
  "QLD",
  "SA",
  "WA",
  "TAS",
  "NT",
  "ACT"
]);

// Property Type - Residential (9 types)
export const propertyTypeResidentialEnum = pgEnum("property_type_residential_enum", [
  "house",
  "unit_apartment",
  "townhouse",
  "villa",
  "duplex",
  "vacant_land",
  "rural_residential",
  "acreage",
  "other_residential"
]);

// Property Type - Commercial (43 types)
export const propertyTypeCommercialEnum = pgEnum("property_type_commercial_enum", [
  "hotel_motel",
  "serviced_apartments",
  "pub_tavern",
  "restaurant_cafe",
  "industrial_warehouse",
  "industrial_manufacturing",
  "industrial_distribution",
  "industrial_light",
  "industrial_heavy",
  "office_cbd",
  "office_suburban",
  "office_strata",
  "office_building",
  "retail_strip",
  "retail_shopping_centre",
  "retail_bulky_goods",
  "retail_freestanding",
  "retail_kiosk",
  "medical_centre",
  "dental_practice",
  "allied_health",
  "aged_care",
  "hospital",
  "childcare_centre",
  "early_learning_centre",
  "mixed_use_retail_residential",
  "mixed_use_office_residential",
  "mixed_use_industrial_residential",
  "showroom",
  "service_station",
  "car_wash",
  "car_dealership",
  "parking_station",
  "storage_facility",
  "data_centre",
  "brewery_winery",
  "quarry_extractive",
  "farm_land",
  "rural_commercial",
  "development_site",
  "land_subdivision",
  "marina",
  "other_commercial"
]);

// Property Status - Residential
export const propertyStatusResidentialEnum = pgEnum("property_status_residential_enum", [
  "owner_occupied",
  "tenanted",
  "vacant"
]);

// Vehicle Type (7 types)
export const vehicleTypeEnum = pgEnum("vehicle_type_enum", [
  "motor_vehicle",
  "motorcycle",
  "caravan",
  "marine_boat",
  "marine_jetski",
  "trailer",
  "other_vehicle"
]);

// Equipment Type (14 types)
export const equipmentTypeEnum = pgEnum("equipment_type_enum", [
  "forklift",
  "access_equipment",
  "agricultural_tractor",
  "agricultural_harvester",
  "agricultural_other",
  "construction_excavator",
  "construction_loader",
  "construction_crane",
  "construction_other",
  "manufacturing",
  "medical_equipment",
  "it_equipment",
  "office_equipment",
  "other_equipment"
]);

// Financial Asset Type (8 types)
export const financialAssetTypeEnum = pgEnum("financial_asset_type_enum", [
  "inventory",
  "debtors",
  "term_deposit",
  "shares_listed",
  "shares_unlisted",
  "managed_funds",
  "bonds",
  "other_financial"
]);

// Tenant Type (26 types - includes 'corporate' and 'individual' for legacy/simplified usage)
export const tenantTypeEnum = pgEnum("tenant_type_enum", [
  "federal_government",
  "state_government",
  "local_government",
  "listed_public_company",
  "private_company",
  "multinational_corporation",
  "franchise_national",
  "franchise_local",
  "national_retailer",
  "major_bank",
  "supermarket_chain",
  "fast_food_chain",
  "restaurant_cafe",
  "hotel_motel",
  "medical_health",
  "legal_accounting",
  "real_estate_agency",
  "child_care_centre",
  "manufacturing",
  "warehousing_logistics",
  "automotive",
  "sme_business",
  "sole_trader",
  "residential_mixed_use",
  "vacant",
  "owner_occupied",
  // Legacy/simplified tenant types (added for backwards compatibility)
  "corporate",
  "individual"
]);

// Encumbrance Type (SEC-RQ5: 18 types)
export const encumbranceTypeEnum = pgEnum("encumbrance_type_enum", [
  "first_mortgage",
  "second_mortgage",
  "subsequent_mortgage",
  "caveat",
  "ppsr_registration",
  "ato_charge",
  "state_revenue_charge",
  "council_charge",
  "water_authority_charge",
  "body_corporate_charge",
  "writ",
  "court_order",
  "easement",
  "covenant",
  "lease_registered",
  "option_to_purchase",
  "profit_a_prendre",
  "other"
]);

// Encumbrance Holder Type (SEC-RQ5: 11 types)
export const holderTypeEnum = pgEnum("holder_type_enum", [
  "bank_lender",
  "non_bank_lender",
  "private_lender",
  "ato",
  "state_revenue",
  "council",
  "water_authority",
  "body_corporate",
  "creditor",
  "court",
  "our_facility"
]);

// Encumbrance Source (SEC-RQ8)
export const encumbranceSourceEnum = pgEnum("encumbrance_source_enum", [
  "manual_entry",
  "title_search",
  "ppsr_search",
  "client_disclosure"
]);

// Encumbrance Discharge Status (SEC-RQ5)
export const encumbranceDischargeStatusEnum = pgEnum("encumbrance_discharge_status_enum", [
  "not_required",
  "discharge_requested",
  "discharge_confirmed",
  "discharge_received",
  "discharged"
]);

// ============================================================================
// TABLES - Security & Property
// ============================================================================

// 1. Securities (Base Polymorphic Table)
export const securities = pgTable("crm_x_securities", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),

  // Canonical deal-level anchor (EDG-1704)
  dealId: uuid("deal_id").references(() => deals.id, { onDelete: "cascade" }),

  // Classification (SEC-RQ1)
  securityType: securityTypeEnum("security_type").notNull(),
  collateralType: collateralSecurityTypeEnum("collateral_type"),
  // security_reference is a GENERATED column - handled by database

  // Lifecycle Status (SEC-RQ7)
  status: securityStatusEnum("status").notNull().default("identified"),

  // Valuation
  estimatedValue: numeric("estimated_value", { precision: 15, scale: 2 }).notNull(),
  valuationDate: date("valuation_date"),
  valuationSource: valuationSourceEnum("valuation_source").default("manual_entry"),
  valuationConfidence: text("valuation_confidence"),

  // Description
  description: text("description"),
  notes: text("notes"),

  // Existing Security Info
  isExistingSecurity: boolean("is_existing_security").default(false),
  existingLenderId: uuid("existing_lender_id").references(() => lenders.id),
  existingLoanBalance: numeric("existing_loan_balance", { precision: 15, scale: 2 }),

  // Multi-tenancy
  organisationId: uuid("organisation_id"),

  // Audit
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
  createdBy: uuid("created_by"),
  updatedBy: uuid("updated_by"),
  deletedAt: timestamp("deleted_at"),
}, (table) => ({
  activeCanonicalKeyUnique: uniqueIndex("crm_x_securities_active_canonical_key_unique")
    .on(
      table.dealId,
      table.securityType,
      sql`lower(btrim(${table.description}))`,
    )
    .where(sql`${table.deletedAt} IS NULL AND nullif(btrim(${table.description}), '') IS NOT NULL`),
}));

// 2. Security Owners (Junction Table - SEC-RQ4)
export const securityOwners = pgTable("crm_x_security_owners", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  securityId: uuid("security_id").references(() => securities.id, { onDelete: "cascade" }).notNull(),

  // Polymorphic Owner (SEC-RQ4: XOR account OR contact)
  ownerAccountId: uuid("owner_account_id").references(() => accounts.id, { onDelete: "cascade" }),
  ownerContactId: uuid("owner_contact_id").references(() => contacts.id, { onDelete: "cascade" }),

  // Ownership Details (SEC-RQ4)
  ownershipPercentage: numeric("ownership_percentage", { precision: 5, scale: 2 }).notNull(),
  ownershipBasis: ownershipBasisEnum("ownership_basis").notNull(),
  isRegisteredProprietor: boolean("is_registered_proprietor").default(true),

  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

// 3. Facility Securities (Junction Table - CRITICAL for LTV)
export const facilitySecurities = pgTable("crm_x_facility_securities", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  facilityId: uuid("facility_id").references(() => facilities.id, { onDelete: "cascade" }).notNull(),
  securityId: uuid("security_id").references(() => securities.id, { onDelete: "cascade" }).notNull(),

  // Priority/Ranking (SEC-RQ3)
  isFirstRanking: boolean("is_first_ranking").default(true),
  securityPosition: securityPositionEnum("security_position").default("first"),
  securedAmount: numeric("secured_amount", { precision: 15, scale: 2 }),
  chargeType: text("charge_type"),

  // LVR Contribution
  includeInLvr: boolean("include_in_lvr").default(true),
  lvrContributionPercentage: numeric("lvr_contribution_percentage", { precision: 5, scale: 2 }).default("100"),

  // Release Conditions
  releaseTrigger: text("release_trigger"),

  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
  deletedAt: timestamp("deleted_at", { withTimezone: true }),
}, (table) => ({
  activeKeyUnique: uniqueIndex("crm_x_facility_securities_active_key_unique")
    .on(table.facilityId, table.securityId)
    .where(sql`${table.deletedAt} IS NULL`),
}));

// 4. Guarantees (CRITICAL - unblocks facility_parties FK from Sprint 3)
export const guarantees = pgTable("crm_x_guarantees", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  facilityId: uuid("facility_id").references(() => facilities.id, { onDelete: "cascade" }).notNull(),

  // Guarantee Type
  guaranteeType: guaranteeTypeEnum("guarantee_type").notNull(),
  liabilityType: liabilityTypeEnum("liability_type").notNull(),

  // Guarantor (either contact or account/company)
  guarantorContactId: uuid("guarantor_contact_id").references(() => contacts.id),
  guarantorAccountId: uuid("guarantor_account_id").references(() => accounts.id),

  // Amount (for limited guarantees)
  limitedAmount: numeric("limited_amount", { precision: 15, scale: 2 }),
  limitedPercentage: numeric("limited_percentage", { precision: 5, scale: 2 }),

  // Status
  status: guaranteeStatusEnum("status").default("proposed"),
  executionDate: date("execution_date"),
  releaseDate: date("release_date"),

  // PPSR Registration (for GSA)
  ppsrRegistrationNumber: text("ppsr_registration_number"),
  ppsrRegistrationDate: date("ppsr_registration_date"),
  ppsrExpiryDate: date("ppsr_expiry_date"),

  // Notes
  notes: text("notes"),

  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
  createdBy: uuid("created_by"),
});

// 5. Security Properties (Extension Table)
export const securityProperties = pgTable("crm_x_security_properties", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  securityId: uuid("security_id").references(() => securities.id, { onDelete: "cascade" }).notNull().unique(),

  // Property Classification
  isResidential: boolean("is_residential").notNull().default(true),
  propertyTypeResidential: propertyTypeResidentialEnum("property_type_residential"),
  propertyTypeCommercial: propertyTypeCommercialEnum("property_type_commercial"),
  propertyStatus: propertyStatusResidentialEnum("property_status"),

  // Address
  addressUnavailable: boolean("address_unavailable").default(false),
  addressLine1: text("address_line_1"),
  addressLine2: text("address_line_2"),
  suburb: text("suburb"),
  state: australianStateEnum("state"),
  postcode: varchar("postcode", { length: 4 }),
  country: text("country").default("Australia"),

  // Title Details
  titleReference: text("title_reference"),
  titleType: text("title_type"),
  registeredOwnersName: text("registered_owners_name"),

  // Land Details (match migration schema)
  landAreaSqm: numeric("land_area_sqm", { precision: 12, scale: 2 }),
  landAreaHectares: numeric("land_area_hectares", { precision: 12, scale: 4 }),
  zoning: text("zoning"),

  // Building Details (match migration schema)
  buildingAreaSqm: numeric("building_area_sqm", { precision: 12, scale: 2 }),
  yearBuilt: integer("year_built"),
  constructionType: text("construction_type"),
  numberOfBedrooms: integer("number_of_bedrooms"),
  numberOfBathrooms: integer("number_of_bathrooms"),
  numberOfCarSpaces: integer("number_of_car_spaces"),

  // Commercial Attributes (match migration schema)
  netLettableAreaSqm: numeric("net_lettable_area_sqm", { precision: 12, scale: 2 }),
  passingRentAnnual: numeric("passing_rent_annual", { precision: 15, scale: 2 }),
  capitalisationRate: numeric("capitalisation_rate", { precision: 5, scale: 2 }),

  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

// 6. Security Valuations (Valuation History)
// NOTE: Schema aligned with actual database columns (Sprint 5 fix)
export const securityValuations = pgTable("crm_x_security_valuations", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  securityId: uuid("security_id").references(() => securities.id, { onDelete: "cascade" }).notNull(),

  valuationDate: date("valuation_date").notNull(),
  valuationAmount: numeric("valuation_amount", { precision: 15, scale: 2 }).notNull(),
  valuationSource: valuationSourceEnum("valuation_source").notNull(),
  valuationReference: text("valuation_reference"),

  // Valuer Details
  valuerName: text("valuer_name"),
  valuerCompany: text("valuer_company"),

  // Valuation Details
  valuationType: text("valuation_type"),
  confidenceLevel: text("confidence_level"),

  // Forced Sale Discount percentage
  fsdPercentage: numeric("fsd_percentage", { precision: 5, scale: 2 }),

  // Rental/Income
  rentalAmount: numeric("rental_amount", { precision: 15, scale: 2 }),

  // Document reference
  documentId: uuid("document_id"),

  // Selection flags
  isCurrent: boolean("is_current").default(false),
  isSelectedForLtv: boolean("is_selected_for_ltv").default(false),
  isPrimary: boolean("is_primary").default(false), // F014: Primary valuation flag

  createdAt: timestamp("created_at").defaultNow().notNull(),
});

// 7. Security Encumbrances (Existing Charges)
// NOTE: Schema aligned with actual database columns (Sprint 5 fix)
export const securityEncumbrances = pgTable("crm_x_security_encumbrances", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  securityId: uuid("security_id").references(() => securities.id, { onDelete: "cascade" }).notNull(),

  encumbranceType: encumbranceTypeEnum("encumbrance_type").notNull(),
  priorityPosition: integer("priority_position"),
  isProposed: boolean("is_proposed").default(false),

  // Holder Details
  holderType: holderTypeEnum("holder_type").notNull(),
  holderName: text("holder_name"),
  holderLenderId: uuid("holder_lender_id"),

  // Amounts
  originalAmount: numeric("original_amount", { precision: 15, scale: 2 }),
  currentBalance: numeric("current_balance", { precision: 15, scale: 2 }),
  balanceAsAt: date("balance_as_at"),
  limitAmount: numeric("limit_amount", { precision: 15, scale: 2 }),

  // Registration Details
  registrationNumber: text("registration_number"),
  registrationDate: date("registration_date"),
  expiryDate: date("expiry_date"),

  // Discharge Details
  dischargeRequired: boolean("discharge_required").default(false),
  dischargeStatus: encumbranceDischargeStatusEnum("discharge_status"),
  payoutFigure: numeric("payout_figure", { precision: 15, scale: 2 }),
  payoutValidUntil: date("payout_valid_until"),
  dischargeAuthoritySent: date("discharge_authority_sent"),
  dischargeReceived: date("discharge_received"),

  // Source
  source: encumbranceSourceEnum("source").default("manual_entry").notNull(),
  sourceDocumentId: uuid("source_document_id"),

  // Verification
  verifiedAt: timestamp("verified_at"),
  verifiedBy: uuid("verified_by"),

  notes: text("notes"),

  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

// 8. Property Tenancies (Tenant Tracking)
// NOTE: FK is to security_properties, NOT securities directly (per migration schema)
export const propertyTenancies = pgTable("crm_x_property_tenancies", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  securityPropertyId: uuid("security_property_id").references(() => securityProperties.id, { onDelete: "cascade" }).notNull(),

  // Tenant Details
  tenantName: text("tenant_name").notNull(),
  tenantType: tenantTypeEnum("tenant_type").notNull(),  // NOT NULL in DB
  tenantAbn: text("tenant_abn"),  // TEXT in DB (not VARCHAR)

  // Lease Details
  leaseStartDate: date("lease_start_date").notNull(),
  leaseEndDate: date("lease_end_date").notNull(),  // NOT NULL in DB
  // leaseTermYears is a GENERATED column in DB - do not include in insert

  // Rent (field names match DB: current_rent_annual, market_rent_annual)
  currentRentAnnual: numeric("current_rent_annual", { precision: 15, scale: 2 }).notNull(),  // NOT NULL in DB
  marketRentAnnual: numeric("market_rent_annual", { precision: 15, scale: 2 }),
  rentReviewType: text("rent_review_type"),
  rentReviewPercentage: numeric("rent_review_percentage", { precision: 5, scale: 2 }),
  rentReviewFrequencyMonths: integer("rent_review_frequency_months").default(12),

  // Area (field names match DB: tenancy_area_sqm, tenancy_area_percentage)
  tenancyAreaSqm: numeric("tenancy_area_sqm", { precision: 12, scale: 2 }),
  tenancyAreaPercentage: numeric("tenancy_area_percentage", { precision: 5, scale: 2 }),

  // Options
  hasOption: boolean("has_option").default(false),
  optionYears: integer("option_years"),
  optionRentReviewType: text("option_rent_review_type"),

  // Security Deposit
  bondAmount: numeric("bond_amount", { precision: 15, scale: 2 }),
  bankGuaranteeAmount: numeric("bank_guarantee_amount", { precision: 15, scale: 2 }),

  // Status
  isVacant: boolean("is_vacant").default(false),
  vacancyStartDate: date("vacancy_start_date"),

  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

// 9. Property Cashflow Projections (NOI Projections)
// NOTE: FK is to security_properties, NOT securities directly (per production DB)
// NOTE: period_start_date and period_end_date are NOT NULL in DB
export const propertyCashflowProjections = pgTable("crm_x_property_cashflow_projections", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  securityPropertyId: uuid("security_property_id").references(() => securityProperties.id, { onDelete: "cascade" }).notNull(),

  projectionYear: integer("projection_year").notNull(),
  periodStartDate: date("period_start_date").notNull(),
  periodEndDate: date("period_end_date").notNull(),

  // Income
  grossRentalIncome: numeric("gross_rental_income", { precision: 15, scale: 2 }).notNull().default("0"),
  otherIncome: numeric("other_income", { precision: 15, scale: 2 }).default("0"),
  vacancyAllowance: numeric("vacancy_allowance", { precision: 15, scale: 2 }).default("0"),

  // Outgoings
  councilRates: numeric("council_rates", { precision: 15, scale: 2 }).default("0"),
  waterRates: numeric("water_rates", { precision: 15, scale: 2 }).default("0"),
  insurance: numeric("insurance", { precision: 15, scale: 2 }).default("0"),
  landTax: numeric("land_tax", { precision: 15, scale: 2 }).default("0"),
  bodyCorporate: numeric("body_corporate", { precision: 15, scale: 2 }).default("0"),
  managementFees: numeric("management_fees", { precision: 15, scale: 2 }).default("0"),
  repairsMaintenance: numeric("repairs_maintenance", { precision: 15, scale: 2 }).default("0"),
  otherOutgoings: numeric("other_outgoings", { precision: 15, scale: 2 }).default("0"),
  capex: numeric("capex", { precision: 15, scale: 2 }).default("0"),

  // Growth assumptions
  outgoingsGrowthRate: numeric("outgoings_growth_rate", { precision: 5, scale: 2 }).default("3.0"),
  rentGrowthRate: numeric("rent_growth_rate", { precision: 5, scale: 2 }).default("3.0"),
  interestRateAssumed: numeric("interest_rate_assumed", { precision: 5, scale: 4 }),
  isAutoCalculated: boolean("is_auto_calculated").default(true),

  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});

// 10. Security Vehicles (Vehicle Extension)
// NOTE: Table columns match production DB (Sprint 5 baseline). Do NOT rename.
export const securityVehicles = pgTable("crm_x_security_vehicles", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  securityId: uuid("security_id").references(() => securities.id, { onDelete: "cascade" }).notNull().unique(),

  vehicleType: vehicleTypeEnum("vehicle_type").notNull(),
  registrationNumber: text("registration_number"),
  registrationState: australianStateEnum("registration_state"),
  vin: text("vin"),
  engineNumber: text("engine_number"),
  hullIdentificationNumber: text("hull_identification_number"),

  make: text("make"),
  model: text("model"),
  variant: text("variant"),
  yearManufactured: integer("year_manufactured"),

  // Attributes
  bodyType: text("body_type"),
  colour: text("colour"),
  odometerKm: integer("odometer_km"),
  engineSizeCc: integer("engine_size_cc"),
  transmission: text("transmission"),
  fuelType: text("fuel_type"),

  // Validation
  validationSource: valuationSourceEnum("validation_source"),
  validationDate: date("validation_date"),
  validationReference: text("validation_reference"),

  // Marine-specific
  lengthMetres: numeric("length_metres", { precision: 10, scale: 2 }),
  beamMetres: numeric("beam_metres", { precision: 10, scale: 2 }),

  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});

// 11. Security Equipment (Equipment Extension)
// NOTE: Table columns match production DB (Sprint 5 baseline). Do NOT rename.
export const securityEquipment = pgTable("crm_x_security_equipment", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  securityId: uuid("security_id").references(() => securities.id, { onDelete: "cascade" }).notNull().unique(),

  equipmentType: equipmentTypeEnum("equipment_type").notNull(),
  serialNumber: text("serial_number"),
  assetTag: text("asset_tag"),
  make: text("make"),
  model: text("model"),
  yearManufactured: integer("year_manufactured"),
  description: text("description"),

  // Condition: CHECK constraint (new, excellent, good, fair, poor)
  condition: text("condition"),
  locationAddress: text("location_address"),
  isMobile: boolean("is_mobile").default(true),
  hoursUsed: integer("hours_used"),
  weightKg: numeric("weight_kg", { precision: 10, scale: 2 }),
  capacity: text("capacity"),

  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});

// 12. Security Financial Assets (Financial Assets Extension)
// NOTE: Table columns match production DB (Sprint 5 baseline). Do NOT rename.
export const securityFinancialAssets = pgTable("crm_x_security_financial_assets", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  securityId: uuid("security_id").references(() => securities.id, { onDelete: "cascade" }).notNull().unique(),

  financialAssetType: financialAssetTypeEnum("financial_asset_type").notNull(),
  description: text("description"),

  // Inventory/Debtors
  inventoryType: text("inventory_type"),
  inventoryLocation: text("inventory_location"),
  debtorAging30: numeric("debtor_aging_30", { precision: 15, scale: 2 }),
  debtorAging60: numeric("debtor_aging_60", { precision: 15, scale: 2 }),
  debtorAging90Plus: numeric("debtor_aging_90_plus", { precision: 15, scale: 2 }),
  concentrationTopDebtorPercent: numeric("concentration_top_debtor_percent", { precision: 5, scale: 2 }),

  // Term deposits / bank accounts
  institutionName: text("institution_name"),
  accountNumber: text("account_number"),

  // Shares
  numberOfUnits: numeric("number_of_units", { precision: 15, scale: 4 }),
  unitPrice: numeric("unit_price", { precision: 15, scale: 4 }),
  asxCode: text("asx_code"),

  // For term deposits
  maturityDate: date("maturity_date"),
  interestRate: numeric("interest_rate", { precision: 5, scale: 4 }),

  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});

// 13. Security Lifecycle Events (Audit Trail)
export const securityLifecycleEvents = pgTable("crm_x_security_lifecycle_events", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  securityId: uuid("security_id").references(() => securities.id, { onDelete: "cascade" }).notNull(),

  eventType: text("event_type").notNull(),
  eventDate: date("event_date").notNull(),
  fromStatus: securityStatusEnum("from_status"),
  toStatus: securityStatusEnum("to_status"),

  description: text("description"),
  notes: text("notes"),

  createdAt: timestamp("created_at").defaultNow().notNull(),
  createdBy: uuid("created_by"),
});

// 14. Property Titles (Title Search Storage)
// NOTE: FK is to security_properties, NOT securities directly (per production DB)
export const propertyTitles = pgTable("crm_x_property_titles", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  securityPropertyId: uuid("security_property_id").references(() => securityProperties.id, { onDelete: "cascade" }).notNull(),

  requestDate: timestamp("request_date", { withTimezone: true }).defaultNow(),
  requestedBy: uuid("requested_by"),
  titleReference: text("title_reference").notNull(),
  state: australianStateEnum("state").notNull(),
  status: text("status").default("requested"),
  availableDate: timestamp("available_date", { withTimezone: true }),
  documentUrl: text("document_url"),
  documentId: uuid("document_id"),
  costAmount: numeric("cost_amount", { precision: 15, scale: 2 }),
  isFreeQuota: boolean("is_free_quota").default(false),

  // JSONB fields for structured data
  registeredProprietors: jsonb("registered_proprietors"),
  encumbrances: jsonb("encumbrances"),
  easements: jsonb("easements"),

  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
});

export const securityMigrationAuditStatusEnum = pgEnum("security_migration_audit_status_enum", [
  "pending",
  "approved",
  "migrated",
  "conflict",
  "skipped",
]);

export const securityMigrationAudit = pgTable("crm_x_security_migration_audit", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  organisationId: uuid("organisation_id")
    .references(() => organisations.id, { onDelete: "cascade" })
    .notNull(),
  canonicalKey: text("canonical_key").notNull(),
  winnerSecurityId: uuid("winner_security_id").references(() => securities.id, { onDelete: "set null" }),
  status: securityMigrationAuditStatusEnum("status").notNull().default("pending"),
  sourceSecurityIds: jsonb("source_security_ids").notNull().default(sql`'[]'::jsonb`),
  sourceRowVersions: jsonb("source_row_versions").notNull().default(sql`'{}'::jsonb`),
  sourceRowChecksums: jsonb("source_row_checksums").notNull().default(sql`'{}'::jsonb`),
  rollbackData: jsonb("rollback_data").notNull().default(sql`'{}'::jsonb`),
  fieldConflicts: jsonb("field_conflicts").notNull().default(sql`'{}'::jsonb`),
  detectedAt: timestamp("detected_at", { withTimezone: true }).defaultNow().notNull(),
  approvedAt: timestamp("approved_at", { withTimezone: true }),
  migratedAt: timestamp("migrated_at", { withTimezone: true }),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
});

// ============================================================================
// ZOD SCHEMAS - Security & Property (SPRINT-5)
// ============================================================================

// Securities Schemas
export const insertSecuritySchema = createInsertSchema(securities).omit({ id: true, createdAt: true, updatedAt: true });
export const updateSecuritySchema = insertSecuritySchema.partial();
export const selectSecuritySchema = createSelectSchema(securities);

// Security Owners Schemas
export const insertSecurityOwnerSchema = createInsertSchema(securityOwners).omit({ id: true, createdAt: true, updatedAt: true });
export const updateSecurityOwnerSchema = insertSecurityOwnerSchema.partial();
export const selectSecurityOwnerSchema = createSelectSchema(securityOwners);

// Facility Securities Schemas
export const insertFacilitySecuritySchema = createInsertSchema(facilitySecurities).omit({ id: true, createdAt: true, updatedAt: true });
export const updateFacilitySecuritySchema = insertFacilitySecuritySchema.partial();
export const selectFacilitySecuritySchema = createSelectSchema(facilitySecurities);

// Guarantees Schemas
export const insertGuaranteeSchema = createInsertSchema(guarantees).omit({ id: true, createdAt: true, updatedAt: true });
export const updateGuaranteeSchema = insertGuaranteeSchema.partial();
export const selectGuaranteeSchema = createSelectSchema(guarantees);

// Security Properties Schemas
export const insertSecurityPropertySchema = createInsertSchema(securityProperties).omit({ id: true, createdAt: true, updatedAt: true });
export const updateSecurityPropertySchema = insertSecurityPropertySchema.partial();
export const selectSecurityPropertySchema = createSelectSchema(securityProperties);

// Security Valuations Schemas
export const insertSecurityValuationSchema = createInsertSchema(securityValuations).omit({ id: true, createdAt: true });
export const updateSecurityValuationSchema = insertSecurityValuationSchema.partial();
export const selectSecurityValuationSchema = createSelectSchema(securityValuations);

// Security Encumbrances Schemas
export const insertSecurityEncumbranceSchema = createInsertSchema(securityEncumbrances).omit({ id: true, createdAt: true, updatedAt: true });
export const updateSecurityEncumbranceSchema = insertSecurityEncumbranceSchema.partial();
export const selectSecurityEncumbranceSchema = createSelectSchema(securityEncumbrances);

// Property Tenancies Schemas
export const insertPropertyTenancySchema = createInsertSchema(propertyTenancies).omit({ id: true, createdAt: true, updatedAt: true });
export const updatePropertyTenancySchema = insertPropertyTenancySchema.partial();
export const selectPropertyTenancySchema = createSelectSchema(propertyTenancies);

// Property Cashflow Projections Schemas
export const insertPropertyCashflowProjectionSchema = createInsertSchema(propertyCashflowProjections).omit({ id: true, createdAt: true, updatedAt: true });
export const updatePropertyCashflowProjectionSchema = insertPropertyCashflowProjectionSchema.partial();
export const selectPropertyCashflowProjectionSchema = createSelectSchema(propertyCashflowProjections);

// Security Vehicles Schemas
export const insertSecurityVehicleSchema = createInsertSchema(securityVehicles).omit({ id: true, createdAt: true, updatedAt: true });
export const updateSecurityVehicleSchema = insertSecurityVehicleSchema.partial();
export const selectSecurityVehicleSchema = createSelectSchema(securityVehicles);

// Security Equipment Schemas
export const insertSecurityEquipmentSchema = createInsertSchema(securityEquipment).omit({ id: true, createdAt: true, updatedAt: true });
export const updateSecurityEquipmentSchema = insertSecurityEquipmentSchema.partial();
export const selectSecurityEquipmentSchema = createSelectSchema(securityEquipment);

// Security Financial Assets Schemas
export const insertSecurityFinancialAssetSchema = createInsertSchema(securityFinancialAssets).omit({ id: true, createdAt: true, updatedAt: true });
export const updateSecurityFinancialAssetSchema = insertSecurityFinancialAssetSchema.partial();
export const selectSecurityFinancialAssetSchema = createSelectSchema(securityFinancialAssets);

// Security Lifecycle Events Schemas
export const insertSecurityLifecycleEventSchema = createInsertSchema(securityLifecycleEvents).omit({ id: true, createdAt: true });
export const updateSecurityLifecycleEventSchema = insertSecurityLifecycleEventSchema.partial();
export const selectSecurityLifecycleEventSchema = createSelectSchema(securityLifecycleEvents);

// Property Titles Schemas
export const insertPropertyTitleSchema = createInsertSchema(propertyTitles).omit({ id: true, createdAt: true });
export const updatePropertyTitleSchema = insertPropertyTitleSchema.partial();
export const selectPropertyTitleSchema = createSelectSchema(propertyTitles);

export const insertSecurityMigrationAuditSchema = createInsertSchema(securityMigrationAudit).omit({
  id: true,
  detectedAt: true,
  approvedAt: true,
  migratedAt: true,
  createdAt: true,
  updatedAt: true,
});
export const updateSecurityMigrationAuditSchema = insertSecurityMigrationAuditSchema.partial();
export const selectSecurityMigrationAuditSchema = createSelectSchema(securityMigrationAudit);

// ============================================================================
// TYPES - Security & Property (SPRINT-5)
// ============================================================================

// Securities Types
export type InsertSecurity = z.infer<typeof insertSecuritySchema>;
export type UpdateSecurity = z.infer<typeof updateSecuritySchema>;
export type Security = typeof securities.$inferSelect;

// Security Owners Types
export type InsertSecurityOwner = z.infer<typeof insertSecurityOwnerSchema>;
export type UpdateSecurityOwner = z.infer<typeof updateSecurityOwnerSchema>;
export type SecurityOwner = typeof securityOwners.$inferSelect;

// Facility Securities Types
export type InsertFacilitySecurity = z.infer<typeof insertFacilitySecuritySchema>;
export type UpdateFacilitySecurity = z.infer<typeof updateFacilitySecuritySchema>;
export type FacilitySecurity = typeof facilitySecurities.$inferSelect;

// Guarantees Types
export type InsertGuarantee = z.infer<typeof insertGuaranteeSchema>;
export type UpdateGuarantee = z.infer<typeof updateGuaranteeSchema>;
export type Guarantee = typeof guarantees.$inferSelect;

// Security Properties Types
export type InsertSecurityProperty = z.infer<typeof insertSecurityPropertySchema>;
export type UpdateSecurityProperty = z.infer<typeof updateSecurityPropertySchema>;
export type SecurityProperty = typeof securityProperties.$inferSelect;

// Security Valuations Types
export type InsertSecurityValuation = z.infer<typeof insertSecurityValuationSchema>;
export type UpdateSecurityValuation = z.infer<typeof updateSecurityValuationSchema>;
export type SecurityValuation = typeof securityValuations.$inferSelect;

// Security Encumbrances Types
export type InsertSecurityEncumbrance = z.infer<typeof insertSecurityEncumbranceSchema>;
export type UpdateSecurityEncumbrance = z.infer<typeof updateSecurityEncumbranceSchema>;
export type SecurityEncumbrance = typeof securityEncumbrances.$inferSelect;

// Property Tenancies Types
export type InsertPropertyTenancy = z.infer<typeof insertPropertyTenancySchema>;
export type UpdatePropertyTenancy = z.infer<typeof updatePropertyTenancySchema>;
export type PropertyTenancy = typeof propertyTenancies.$inferSelect;

// Property Cashflow Projections Types
export type InsertPropertyCashflowProjection = z.infer<typeof insertPropertyCashflowProjectionSchema>;
export type UpdatePropertyCashflowProjection = z.infer<typeof updatePropertyCashflowProjectionSchema>;
export type PropertyCashflowProjection = typeof propertyCashflowProjections.$inferSelect;

// Security Vehicles Types
export type InsertSecurityVehicle = z.infer<typeof insertSecurityVehicleSchema>;
export type UpdateSecurityVehicle = z.infer<typeof updateSecurityVehicleSchema>;
export type SecurityVehicle = typeof securityVehicles.$inferSelect;

// Security Equipment Types
export type InsertSecurityEquipment = z.infer<typeof insertSecurityEquipmentSchema>;
export type UpdateSecurityEquipment = z.infer<typeof updateSecurityEquipmentSchema>;
export type SecurityEquipment = typeof securityEquipment.$inferSelect;

// Security Financial Assets Types
export type InsertSecurityFinancialAsset = z.infer<typeof insertSecurityFinancialAssetSchema>;
export type UpdateSecurityFinancialAsset = z.infer<typeof updateSecurityFinancialAssetSchema>;
export type SecurityFinancialAsset = typeof securityFinancialAssets.$inferSelect;

// Security Lifecycle Events Types
export type InsertSecurityLifecycleEvent = z.infer<typeof insertSecurityLifecycleEventSchema>;
export type UpdateSecurityLifecycleEvent = z.infer<typeof updateSecurityLifecycleEventSchema>;
export type SecurityLifecycleEvent = typeof securityLifecycleEvents.$inferSelect;

export type InsertSecurityMigrationAudit = z.infer<typeof insertSecurityMigrationAuditSchema>;
export type UpdateSecurityMigrationAudit = z.infer<typeof updateSecurityMigrationAuditSchema>;
export type SecurityMigrationAudit = typeof securityMigrationAudit.$inferSelect;

// Property Titles Types
export type InsertPropertyTitle = z.infer<typeof insertPropertyTitleSchema>;
export type UpdatePropertyTitle = z.infer<typeof updatePropertyTitleSchema>;
export type PropertyTitle = typeof propertyTitles.$inferSelect;
